/**
 * سربرگِ صفِ بازبینی — حالا ابزارِ کامل، نه فقط شمارش و لینک.
 *
 * ═══ چرا کپی نشد و از نو نوشته شد (گزینه‌ی «ج») ═══
 *
 * صفحه‌های `AdminPendingPage`/`AdminItemPage` در apps/web از UI kitِ همان اپ
 * می‌خواندند (`@/ui/kit`) و گاردِ مونوریپو `app→app` را ممنوع می‌کند — پس
 * جابه‌جایی سه راه داشت:
 *
 *   الف) کپیِ kitِ سایت به apps/admin — ساده، ولی دو kit که از هم دور می‌شوند.
 *   ب) بسته‌ی مشترکِ `packages/ui-web` — تمیزتر، ولی ورک‌اسپیسِ پنجم و گاردِ
 *      تازه برای دو مصرف‌کننده.
 *   ج) چند کامپوننتِ کوچک در خودِ apps/admin، متناسبِ چگالیِ خودش.
 *
 * «ج» انتخاب شد. اپِ ادمین عمداً چگال‌تر از سایت است — جدولِ فشرده، نه کارتِ
 * بزرگ با عکس — و کپی کردنِ kitِ سایت یعنی وارد کردنِ زبانِ بصریِ اشتباه به
 * جایی که مخاطبش می‌خواهد بیست ردیف را یک‌جا ببیند. «ب» هم برای دو
 * مصرف‌کننده زودرس است: بسته‌ی مشترک وقتی ارزش دارد که اشتراکِ واقعی باشد،
 * نه وقتی دو اپ عمداً دو زبانِ بصریِ متفاوت دارند.
 *
 * منطق در `packages/core/src/data/adminRepo.ts` می‌ماند و هر سه اپ از همان
 * یکی می‌خوانند — چیزی که واقعاً مشترک است، همان است.
 *
 * نسخه‌ی `apps/web` با همین تغییر حذف شد؛ دو جا برای یک کار یعنی یکی‌شان
 * کهنه می‌شود.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminRepo, itemLabel } from '@/core/data/adminRepo';
import { eventRepo } from '@/core/data/miscRepo';
import { opsRepo } from '@/core/data/opsRepo';
import { formatDate } from '@/core/lib/dates';
import { reportError } from '@/core/lib/report';
import type { EventItem } from '@/core/domain/types';
import { Samples } from '../crawler/Samples';
import { Stat } from '@/ui/Num';
import { ItemDetail } from './ItemDetail';
import { Modal } from '@/ui/Modal';

const fmt = (n: number) => n.toLocaleString('en-US');
const domainOf = (url?: string | null): string =>
  (url ? url.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '') : '');

type Queue = 'pending' | 'flagged' | 'merged';

export function ReviewTab() {
  const qc = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const [queue, setQueue] = useState<Queue>('pending');
  const [rejectTarget, setRejectTarget] = useState<EventItem | null>(null);
  const [reason, setReason] = useState('');
  const [goto, setGoto] = useState('');
  const [gotoErr, setGotoErr] = useState<string | null>(null);

  // همان کلیدِ سربرگِ کرالر با شهرِ خالی — پس دو صفحه یک بار کوئری می‌زنند.
  const funnel = useQuery({ queryKey: ['funnel', ''], queryFn: () => opsRepo.crawlerFunnel() });
  const pending = useQuery({ queryKey: ['eventsPendingReview'], queryFn: () => eventRepo.pendingReview() });
  const flagged = useQuery({ queryKey: ['adminFlagged'], queryFn: adminRepo.flagged });
  const merged = useQuery({
    queryKey: ['adminMergedList'], queryFn: adminRepo.merged, enabled: queue === 'merged',
  });

  const invalidate = () => {
    for (const key of [['eventsPendingReview'], ['adminFlagged'], ['adminMergedList']]) {
      qc.invalidateQueries({ queryKey: key });
    }
  };

  const approve = useMutation({
    mutationFn: (id: string) => eventRepo.approve(id),
    onSuccess: invalidate,
    onError: (err) => reportError('admin.review.approve', err),
  });
  const reject = useMutation({
    mutationFn: () => eventRepo.reject(rejectTarget!.id, reason.trim()),
    onSuccess: () => { setRejectTarget(null); setReason(''); invalidate(); },
    onError: (err) => reportError('admin.review.reject', err),
  });
  const jump = useMutation({
    mutationFn: () => adminRepo.byShortId(Number(goto.trim())),
    onSuccess: (found) => {
      if (!found) { setGotoErr(`#${goto.trim()} پیدا نشد.`); return; }
      setGoto(''); setGotoErr(null);
      setOpenId(found.id);
    },
    onError: (err) => reportError('admin.review.jump', err),
  });

  // یک آیتمِ باز، کلِ سربرگ را می‌گیرد. اپِ ادمین روتر ندارد و برای یک ابزارِ
  // داخلی هم لازم ندارد؛ «برگشت» همان چیزی است که از یک صفحه انتظار می‌رود.
  if (openId) {
    return <ItemDetail id={openId} onBack={() => setOpenId(null)} onOpen={setOpenId} />;
  }

  const items = pending.data ?? [];
  const flaggedItems = flagged.data ?? [];
  const mergedItems = merged.data ?? [];
  const d = funnel.data;

  const rows = queue === 'pending' ? items : queue === 'flagged' ? flaggedItems : mergedItems;

  return (
    <>
      {d ? (
        <div className="grid n3" style={{ marginTop: 12 }}>
          {/* ⚠ هر سه عدد با vNext عوض شدند و این جانشین‌گذاری است، نه
              تغییرِ سلیقه: `crawler.review_queue` و `crawler.alerts` هر دو
              ۲۰۲۶-۰۸-۱۹ حذف شدند. معادلِ صفِ کرالر حالا حکمِ `review`ِ
              سیاستِ انتشار است، و جای «هشدار» را حکمِ آدم گرفته که در
              `crawler.labels` می‌نشیند. */}
          <Stat label="حکمِ «بازبینی» در کرالر" v={d.policy_review} />
          {/* ⚠ این یکی جفت نمی‌شود و نباید بشود: صفِ امروزِ اپ است، نه
              شمارشِ یک پنجره. «۴ تا در ۱۰ روز» جوابِ سوالِ دیگری است. */}
          <div className="card stat">
            <div className="n">{fmt(items.length)}</div>
            <div className="k">منتظرِ تاییدِ ادمین در اپ</div>
          </div>
          <Stat label="حکمِ ثبت‌شده‌ی آدم" v={d.labels_total} />
        </div>
      ) : null}

      <div className="card">
        <h2>صفِ بازبینی</h2>

        <div className="filters" style={{ marginBottom: 10 }}>
          <input value={goto} dir="ltr" placeholder="شناسه‌ی عددی"
            onChange={(ev) => { setGoto(ev.target.value); setGotoErr(null); }}
            onKeyDown={(ev) => { if (ev.key === 'Enter' && goto.trim()) jump.mutate(); }} />
          <button className="btn" type="button" disabled={!goto.trim() || jump.isPending}
            onClick={() => jump.mutate()}>باز کن</button>
          {gotoErr ? <span className="err">{gotoErr}</span> : null}
        </div>

        <div className="tabs" style={{ marginBottom: 8 }}>
          <button type="button" className={`tab${queue === 'pending' ? ' on' : ''}`}
            onClick={() => setQueue('pending')}>در انتظار ({items.length})</button>
          <button type="button" className={`tab${queue === 'flagged' ? ' on' : ''}`}
            onClick={() => setQueue('flagged')}>نشان‌دار ({flaggedItems.length})</button>
          <button type="button" className={`tab${queue === 'merged' ? ' on' : ''}`}
            onClick={() => setQueue('merged')}>ادغام‌شده</button>
        </div>

        {(queue === 'pending' && pending.isLoading)
          || (queue === 'merged' && merged.isLoading) ? <p className="dim">…</p> : null}

        {rows.length === 0 && !pending.isLoading ? (
          <p className="dim">
            {queue === 'pending' ? 'صف خالی است.'
              : queue === 'flagged' ? 'چیزی نشان‌دار نشده.' : 'ادغامی ثبت نشده.'}
          </p>
        ) : null}

        {rows.length > 0 ? (
          <div className="scrollx">
            <table className="tbl">
              <thead>
                <tr>
                  <th>#</th><th>عنوان</th><th>کِی</th><th>کجا</th><th>منبع</th>
                  <th>{queue === 'merged' ? 'ادغام در' : ''}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => (
                  <tr key={e.id}>
                    <td className="mono dim">{itemLabel(e)}</td>
                    <td>
                      {/* عنوان خودش دکمه است: در جدولِ فشرده، ستونِ جداگانه
                          برای «باز کن» یعنی یک ستون کمتر برای داده. */}
                      <button className="linkbtn" type="button" onClick={() => setOpenId(e.id)}>
                        {e.title}
                      </button>
                    </td>
                    <td className="dim">{e.startsAt ? formatDate(e.startsAt) : '—'}</td>
                    <td className="dim">{e.venueName ?? '—'}</td>
                    <td className="dim mono">{domainOf(e.url)}</td>
                    <td>
                      {queue === 'pending' ? (
                        <div className="chiprow">
                          <button className="btn" type="button" disabled={approve.isPending}
                            onClick={() => approve.mutate(e.id)}>تایید</button>
                          <button className="btn ghost" type="button"
                            onClick={() => setRejectTarget(e)}>رد</button>
                        </div>
                      ) : queue === 'merged' ? (
                        <span className="dim mono">
                          #{(e as { mergedIntoShortId?: number | null }).mergedIntoShortId ?? '?'}
                        </span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      <div className="card">
        <h2>حکمِ «بازبینی» در کرالر</h2>
        <p className="dim" style={{ marginBottom: 6 }}>
          خوشه‌هایی که سیاستِ انتشار نه «آری» گفت نه «نه». ستونِ دلیل می‌گوید
          چرا. تا وقتی این‌جا بمانند به اپ نمی‌روند.
        </p>
        <Samples kind="policy_review" limit={10} />
      </div>

      <div className="card">
        <h2>آخرین منتشرشده‌ها</h2>
        <Samples kind="published" limit={10} />
      </div>

      {rejectTarget ? (
        <Modal title="ردِ آیتم" hint={rejectTarget.title} onClose={() => setRejectTarget(null)}>
          <textarea className="input textarea" value={reason}
            onChange={(ev) => setReason(ev.target.value)} placeholder="دلیل" />
          <button className="btn block" type="button"
            disabled={reason.trim().length === 0 || reject.isPending}
            onClick={() => reject.mutate()}>{reject.isPending ? '…' : 'رد کن'}</button>
        </Modal>
      ) : null}
    </>
  );
}
